import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ViewProps } from 'react-native'
import { Button } from 'zens-storybook'
import { AccessType } from 'zens-store'

import { usePermissions } from '@dms/hook'

import { InfoTable } from '../info'

import { useCompanySubscriptions } from './use-subscriptions'

interface Props extends ViewProps {
  companyId: number
}

const CompanySubscriptions = ({ companyId, ...props }: Props) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions(companyId)
  const canViewSubscriptions = hasPermission(AccessType.Read, 'settings')
  const canEditSubscriptions = hasPermission(AccessType.Update, 'settings')
  const { data, error, loading, reload } = useCompanySubscriptions(companyId, canViewSubscriptions)

  useEffect(() => {
    if (!canViewSubscriptions || typeof window === 'undefined') return
    window.addEventListener('focus', reload)
    return () => window.removeEventListener('focus', reload)
  }, [reload, canViewSubscriptions])

  if (!canViewSubscriptions) return null

  const subscriptions = data?.subscriptions.filter((item) => item.plan_code !== null) ?? []
  const fields =
    loading || error
      ? [
          {
            label: t('Subscriptions'),
            value: error ? t('Subscriptions could not be loaded') : t('Loading...'),
          },
        ]
      : subscriptions.length
        ? subscriptions.map((item) => ({
            label: t(item.name),
            value: t(item.plan_name || item.plan_code || 'Not subscribed'),
          }))
        : [{ label: t('Subscriptions'), value: t('No subscriptions') }]

  return (
    <InfoTable
      {...props}
      companyId={companyId}
      title={t('Subscriptions')}
      buttonTitle={t('Edit')}
      buttonHref={data && canEditSubscriptions ? `/companies/${companyId}/subscriptions` : undefined}
      config={{ fields }}
      unfolded={false}
      testID="CompanySubscriptions"
    >
      {error && <Button light title={t('Retry')} onPress={reload} />}
    </InfoTable>
  )
}

export default CompanySubscriptions
